import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { LogOut, Users, Truck, DollarSign, Activity, Clock, BookOpen } from "lucide-react";
import AiIntegrationPanel from "@/components/AiIntegrationPanel";
import TiersPanel from "@/components/TiersPanel";
import ZonesPanel from "@/components/ZonesPanel";
import AdminTicketsPanel from "@/components/AdminTicketsPanel";
import AdminOpsPanel from "@/components/AdminOpsPanel";
import AdminManagementPanel from "@/components/AdminManagementPanel";
import PriorityQueuePanel from "@/components/PriorityQueuePanel";
import UsersManagementPanel from "@/components/UsersManagementPanel";
import BookingsManagementPanel from "@/components/BookingsManagementPanel";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800 border-amber-400",
  accepted: "bg-blue-100 text-asap-blue border-asap-blue",
  en_route: "bg-orange-100 text-asap-orange border-asap-orange",
  on_scene: "bg-violet-100 text-violet-800 border-violet-500",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-500",
  cancelled: "bg-slate-100 text-slate-600 border-slate-400",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("priority");

  useEffect(() => {
    const load = async () => {
      try {
        const [s, u, b] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/bookings")]);
        setStats(s.data);
        setUsers(u.data);
        setBookings(b.data);
      } catch {}
    };
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, []);

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <header className="bg-white border-b-2 border-asap-deep sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-5 flex justify-between items-center gap-2">
          <div className="flex items-center gap-2 sm:gap-6 min-w-0">
            <Logo size={64} />
            <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.3em] font-bold bg-asap-red text-white px-2 py-1">Admin Console</span>
          </div>
          <div className="flex gap-1 sm:gap-3 items-center shrink-0">
            <button onClick={() => nav("/manual")} data-testid="open-manual-btn" title="Operations Manual" aria-label="Operations Manual" className="inline-flex items-center gap-2 px-2 sm:px-3 py-2 bg-asap-yellow text-asap-deep border-2 border-asap-deep font-bold uppercase text-xs tracking-wider hover:translate-x-[1px] hover:translate-y-[1px] transition">
              <BookOpen size={14}/><span className="hidden sm:inline"> Operations Manual</span>
            </button>
            <div className="text-sm font-bold text-asap-deep hidden lg:block">{user.name}</div>
            <button data-testid="admin-logout" onClick={doLogout} aria-label="Logout" className="flex items-center gap-2 px-2 sm:px-3 py-2 border-2 border-asap-deep text-asap-deep hover:bg-asap-deep hover:text-white text-sm font-bold uppercase"><LogOut size={14}/><span className="hidden sm:inline"> Logout</span></button>
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-3 sm:px-6 flex gap-1 overflow-x-auto no-scrollbar" data-testid="admin-tabs">
          {[
            { k: "priority", l: `Priority Queue${stats?.bookings?.owner_review ? ` (${stats.bookings.owner_review})` : ""}` },
            { k: "overview", l: "Overview" },
            { k: "bookings", l: "Bookings" },
            { k: "users", l: "Users" },
            { k: "admins", l: "Admins" },
            { k: "ops", l: "Ops" },
            { k: "zones", l: "Zones" },
            { k: "tickets", l: "Tickets" },
            { k: "tiers", l: "Tiers" },
            { k: "ai", l: "AI / Webhooks" },
          ].map((t) => (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)} className={`shrink-0 px-3 sm:px-4 py-2 text-[11px] sm:text-xs uppercase tracking-[0.2em] font-bold border-b-4 whitespace-nowrap transition ${tab === t.k ? "border-asap-orange text-asap-deep" : "border-transparent text-slate-500 hover:text-asap-deep"}`}>{t.l}</button>
          ))}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8 space-y-4 sm:space-y-6">
        {tab === "priority" && <PriorityQueuePanel />}
        {tab === "overview" && stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Users" value={stats.users.total} sub={`${stats.users.customers} customers · ${stats.users.drivers} drivers`} />
              <StatCard icon={Activity} label="Drivers Online" value={stats.users.drivers_online} sub={`of ${stats.users.drivers}`} accent />
              <StatCard icon={Truck} label="Active Jobs" value={stats.bookings.active} sub={`${stats.bookings.pending} pending`} />
              <StatCard icon={DollarSign} label="Revenue" value={`$${stats.revenue.toFixed(2)}`} sub={`${stats.bookings.completed} completed`} />
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-5">
              <h3 className="font-display uppercase text-asap-deep mb-3 flex items-center gap-2"><Clock size={18}/> Latest Bookings</h3>
              <BookingTable rows={bookings.slice(0, 10)} />
            </div>
          </>
        )}

        {tab === "bookings" && <BookingsManagementPanel />}

        {tab === "users" && <UsersManagementPanel currentUserId={user?.id} />}

        {tab === "admins" && <AdminManagementPanel currentUserId={user?.id}/>}

        {tab === "tiers" && <TiersPanel />}
        {tab === "zones" && <ZonesPanel />}
        {tab === "tickets" && <AdminTicketsPanel />}
        {tab === "ops" && (
          <div className="space-y-6">
            <AdminOpsPanel />
          </div>
        )}
        {tab === "ai" && <AiIntegrationPanel />}
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className={`p-5 border rounded-md ${accent ? "bg-asap-deep text-white border-asap-deep" : "bg-white border-slate-200"}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`flex items-center justify-between ${accent ? "text-asap-yellow" : "text-asap-orange"}`}>
        <div className="text-xs uppercase tracking-[0.25em] font-bold">{label}</div>
        <Icon size={20}/>
      </div>
      <div className="font-display text-3xl mt-2">{value}</div>
      <div className={`text-xs mt-1 ${accent ? "text-white/60" : "text-slate-500"}`}>{sub}</div>
    </div>
  );
}

function BookingTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="bookings-table">
        <thead className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
          <tr><th className="py-2">Service</th><th>Customer</th><th>Driver</th><th>Status</th><th>Payment</th><th className="text-right">Price</th></tr>
        </thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.id} className="border-b last:border-0" data-testid={`booking-row-${b.id}`}>
              <td className="py-2 font-bold text-asap-deep">{b.service_name}</td>
              <td className="text-slate-600">{b.customer_name}</td>
              <td className="text-slate-600">{b.driver_name || "—"}</td>
              <td><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border font-bold ${STATUS_COLORS[b.status]}`}>{b.status}</span></td>
              <td><span className={`text-[10px] uppercase tracking-wider font-bold ${b.payment_status === "paid" ? "text-emerald-600" : "text-slate-500"}`}>{b.payment_status}</span></td>
              <td className="text-right font-display text-asap-orange">${b.price.toFixed(2)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">No bookings</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
